import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getI18n } from '@/lib/i18n';
import api, { formatApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Save, ArrowLeft, KeyRound, UserCog } from 'lucide-react';

// Пытаемся разобрать ФИО на Имя и Фамилию (если first/last ещё не заполнены отдельно).
function splitContactName(full) {
  const s = (full || '').trim();
  if (!s) return { first: '', last: '' };
  const parts = s.split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const { t } = getI18n(user);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: '', last_name: '', company_name: '', tender_direction: 'logistics',
    current_password: '', new_password: '', new_password_confirm: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fromSplit = splitContactName(user.contact_name);
    setForm(p => ({
      ...p,
      first_name: user.first_name || fromSplit.first || '',
      last_name: user.last_name || fromSplit.last || '',
      company_name: user.company_name || '',
      tender_direction: user.tender_direction || 'logistics',
    }));
  }, [user]);

  const update = (key) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm(p => ({ ...p, [key]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const wantsPwChange = form.new_password || form.new_password_confirm || form.current_password;
    if (wantsPwChange) {
      if (!form.current_password) return toast.error(t('profile.err.current'));
      if (!form.new_password || form.new_password.length < 6) return toast.error(t('profile.err.short'));
      if (form.new_password !== form.new_password_confirm) return toast.error(t('profile.err.mismatch'));
    }
    setLoading(true);
    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        company_name: form.company_name,
      };
      if (user?.role === 'organizer') payload.tender_direction = form.tender_direction;
      if (wantsPwChange) {
        payload.current_password = form.current_password;
        payload.new_password = form.new_password;
      }
      await api.put('/auth/profile', payload);
      await refreshUser();
      setForm(p => ({ ...p, current_password: '', new_password: '', new_password_confirm: '' }));
      toast.success(t('profile.updated'));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (!user) return null;

  return (
    <div className="p-3" data-testid="profile-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <UserCog className="w-6 h-6 text-[#0046AD]" /> {t('profile.title')}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{t('profile.subtitle')}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="profile-back-btn" className="rounded-sm">
          <ArrowLeft className="w-4 h-4 mr-1" /> {t('profile.back')}
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm space-y-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">{t('profile.personal')}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">{t('profile.first_name')}</Label>
              <Input value={form.first_name} onChange={update('first_name')} data-testid="profile-first-name" className="border-slate-300 rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">{t('profile.last_name')}</Label>
              <Input value={form.last_name} onChange={update('last_name')} data-testid="profile-last-name" className="border-slate-300 rounded-sm" />
            </div>
          </div>
          <div className="mt-3 space-y-1.5">
            <Label className="text-sm font-medium text-slate-700">{t('profile.email')}</Label>
            <Input value={user.email || ''} disabled data-testid="profile-email" className="border-slate-300 rounded-sm bg-slate-50 text-slate-500" />
            <p className="text-[11px] text-slate-400">{t('profile.email.hint')}</p>
          </div>
        </div>

        <Separator />

        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">{t('profile.company_block')}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">{t('profile.company')}</Label>
              <Input value={form.company_name} onChange={update('company_name')} data-testid="profile-company" className="border-slate-300 rounded-sm" />
            </div>
            {user.role === 'organizer' && (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-slate-700">{t('profile.direction')}</Label>
                <Select value={form.tender_direction} onValueChange={v => setForm(p => ({ ...p, tender_direction: v }))}>
                  <SelectTrigger data-testid="profile-direction" className="border-slate-300 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="logistics">{t('profile.dir.logistics')}</SelectItem>
                    <SelectItem value="purchase">{t('profile.dir.purchase')}</SelectItem>
                    <SelectItem value="purchase_oil">{t('profile.dir.purchase_oil')}</SelectItem>
                    <SelectItem value="all">{t('profile.dir.all')}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-400">{t('profile.direction.hint')}</p>
              </div>
            )}
          </div>
        </div>

        <Separator />

        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
            <KeyRound className="w-3.5 h-3.5" /> {t('profile.pw.title')} <span className="text-[10px] normal-case text-slate-400 tracking-normal">{t('profile.pw.optional')}</span>
          </p>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">{t('profile.pw.current')}</Label>
              <Input type="password" value={form.current_password} onChange={update('current_password')}
                autoComplete="current-password" data-testid="profile-current-pw" className="border-slate-300 rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-slate-700">{t('profile.pw.new')}</Label>
                <Input type="password" value={form.new_password} onChange={update('new_password')}
                  autoComplete="new-password" data-testid="profile-new-pw" className="border-slate-300 rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-slate-700">{t('profile.pw.confirm')}</Label>
                <Input type="password" value={form.new_password_confirm} onChange={update('new_password_confirm')}
                  autoComplete="new-password" data-testid="profile-new-pw-confirm" className="border-slate-300 rounded-sm" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={loading} data-testid="profile-save-btn"
            className="bg-[#0046AD] hover:bg-[#003A8C] rounded-sm font-semibold">
            <Save className="w-4 h-4 mr-2" /> {loading ? t('profile.saving') : t('profile.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
